import { T, type Trilingual } from './i18n.ts';

/**
 * 额度盘点：16 项额度里，哪几项**真的会拦住你**，哪几项只是一个数字。
 *
 * ════════════════════════════════════════════════════════
 * 为什么要有这份登记
 * ════════════════════════════════════════════════════════
 * 账号页那张「档位与额度」表是把服务器下发的 `entitlements.quotas`
 * **整个列出来**的（`AccountPage.tsx`），一项不漏。而逐项核过源码之后：
 * 16 项里有 **6 项没有任何代码在执行**、1 项只显示不执行。
 *
 * 也就是说一个付了钱的人会在自己的账号页上读到
 * 「数据导出次数 3 次/天」、「自定义图标位数 10 个」——
 * 而这两个功能**一行代码都没有**。他看到的是一个数字，
 * 以为那是一项能用的能力。
 *
 * 这件事在这个仓库里有过先例，而且是同一个形状：`memos` 那一行额度
 * （免费 50 / VIP 500）早就在生产库里、显示在账号页和套餐表上，
 * 而备忘录功能一行代码都没有 —— 那才是做备忘录的真正理由
 * （`docs/现状与欠账.md` 2.8），不是「侧栏里有一页是空的」。
 * 剩下这几项是同一个坑里还没捞出来的部分。
 *
 * 所以这里按「网站如实」那条规矩办（`docs/现状与欠账.md` 4.6）：
 * **要么起作用，要么在界面上标出来。**
 *
 * ════════════════════════════════════════════════════════
 * 这份登记怎么防止自己过期
 * ════════════════════════════════════════════════════════
 * 一份手写的「哪些还没做」清单最后一定会变成谎话 —— 功能做上了，
 * 清单还在说「还没做」，而那比不标更坏（用户会以为我们在骗他，
 * 或者干脆不信界面上任何一句话）。
 *
 * 所以 `quota-wiring.test.ts` 从两个方向盯着它：
 *   1. **种子数据里的每一项都必须在这里被分类**（漏一项就红）——
 *      新加一项额度时，必须顺手说清它起不起作用。
 *   2. **标成「起作用」的，执行点文件里必须真的出现这个键**（删掉就红）；
 *      **标成「不起作用」的，执行点文件里必须一次都不出现**（接上就红）。
 *
 * 第 2 条为什么只扫那几个文件、不扫全仓：这些键名太普通了。
 * `manual`、`api`、`export`、`chain` 这些词在源码里到处都是
 * （`rules/engine.ts` 里的 `manual`、`schema/money.ts` 里的 `chain`…），
 * 全仓扫字面量的话每一项都会被判成「有人读」—— 那条测试会永远绿，
 * 也就永远没用。执行点是固定的那几个文件，扫它们才有判别力。
 */

/**
 * 真正在执行配额的那几个文件。扫描只看这里。
 *
 * 路径相对仓库根。加新的执行点时要加进来，否则那一项会被判成「不起作用」。
 */
export const QUOTA_ENFORCERS: readonly string[] = [
  'packages/wallets/src/quota.ts',
  'packages/tokens/src/quota.ts',
  'packages/scheduler/src/quota.ts',
  'packages/memos/src/store.ts',
  'packages/reminders/src/store.ts',
  'packages/fomo/src/store.ts',
  'packages/heat/src/store.ts',
  'apps/api/src/routes/export.ts',
  'apps/api/src/user-rate.ts',
  'apps/web/src/lib/user-icons.ts',
  'packages/scheduler/src/retention.ts'
  /* 刻意不放 apps/api/src/app.ts：它里面有 `ent.quotas['keepdays']`，
     但那只是把数字**下发给收件箱页显示**，不是执行 —— 放进来的话
     keepdays 会被判成「起作用」，而它一条记录都没删过。 */
];

/**
 * 会真的拦住你的那几项：超了就加不进去 / 发不出去 / 查得更慢。
 *
 * 每一项后面是**执行点**，核对过的（2026-10-01）。
 */
export const QUOTA_ENFORCED: readonly string[] = [
  'wallets',    /* packages/wallets/src/quota.ts + app.ts 的 POST /api/wallets */
  'tokens',     /* packages/tokens/src/quota.ts 的 tokenLimit */
  'pricerules', /* packages/tokens/src/quota.ts 的 ruleLimit */
  'reminders',  /* packages/reminders/src/store.ts */
  'memos',      /* packages/memos/src/store.ts */
  'channels',   /* packages/scheduler/src/quota.ts：调度器截通道数 */
  'dailysend',  /* packages/scheduler/src/quota.ts：每日上限 */
  'price',      /* packages/tokens/src/quota.ts 的 priceIntervalSec */
  'wallet',     /* packages/wallets/src/quota.ts：扩展采集间隔 */
  'chains',     /* packages/heat/src/store.ts 的 setUserChains：超额就拒（2026-10-29 链热度第一版） */
  'fomoresist', /* packages/fomo/src/store.ts 的 recordResist：事务里锁住用户那一行再数，并发下也不会多插（2026-10-28） */
  'manual',     /* packages/tokens/src/quota.ts 的 manualCheckSec：「立即检查一轮」两次之间的冷却（2026-10-28 按钮上了价格页） */
  'export',     /* apps/api/src/routes/export.ts：按 data_export 表最近 24 小时导出了几次，超了拒（2026-10-29） */
  'api',        /* apps/api/src/user-rate.ts：认出是谁之后按账号每分钟数一次（2026-10-29，150 / 300） */
  'icons',      /* apps/web/src/lib/user-icons.ts 的 setIcon：换满这一档的个数就加不进去（2026-10-29；图存在他自己的浏览器里） */
  'keepdays'    /* packages/scheduler/src/retention.ts 的 pruneByPlan：每小时按档位删提醒记录、成交流水、设置记录、喊话记录（2026-10-30） */
];

/**
 * 还不起作用的那几项，以及**对用户说什么**。
 *
 * 文案的写法有一条硬规矩：**说清「现在实际会发生什么」，不说「我们还没做」**。
 * 「还没做」是我们的内部状态，他关心的是「这个数字管不管我」。
 * 比如保留天数那一条，真实情况是「不但没删，而且一条都没删」——
 * 对他是好事，所以直接说清，而不是含糊成「此功能开发中」。
 */
export const QUOTA_INERT: Readonly<Record<string, Trilingual>> = {
  chain: T(
    '这个间隔不起作用：链热度全站一份、统一每 10 分钟更新一次，和档位无关（多刷几次不多花服务器的额度）。',
    '這個間隔不起作用：鏈熱度全站一份、統一每 10 分鐘更新一次，和檔位無關（多刷幾次不多花伺服器的額度）。',
    'This interval does nothing: chain heat is fetched once for the whole site every 10 minutes, whatever your plan (refreshing more often would not cost us anything).'
  )
};

/** 这一项额度要不要在账号页上带一句说明。不用就是 null */
export function quotaNote(key: string): Trilingual | null {
  return QUOTA_INERT[key] ?? null;
}

/** 这一项额度现在真的会拦住人吗 */
export function quotaWorks(key: string): boolean {
  return QUOTA_ENFORCED.includes(key);
}
